import React from 'react';
import {colors} from '@/styles/tokens';

/**
 * Rating — the app's read-only star-rating primitive (replaces MUI `Rating`).
 *
 * It owns the structural grammar only: DOM, semantics, accessibility, the inline
 * SVG stars and the half-star clip. Visual identity — star colours — is supplied
 * as token-sourced CSS custom properties (see `ratingVars`), and layout/spacing
 * around the stars stays consumer-owned via `className`, the same split shipped
 * for `Chip` and `ScrollableTabs`. The primitive is display-only by design; any
 * future interactivity is an additive, non-breaking change.
 *
 * Renders raw `<span>`/`<svg>` (not MUI components), so styling is inline
 * `style` objects rather than `sx` — there is no static class to extract.
 */

// Design tokens injected as CSS custom properties, consumed by the inline
// styles below. Single source of truth is src/styles/tokens.ts. Defined once
// at module scope — static, so it is never recreated per render. The variable
// names carry the component semantics; the tokens stay pure design concepts
// (an accent + a neutral), with no component-scoped token introduced.
const ratingVars = {
    '--rating-filled': colors.accent.starYellow,
    '--rating-empty': colors.neutral.gray
} as React.CSSProperties;

const rootBaseStyle: React.CSSProperties = {display: 'inline-flex', alignItems: 'center', lineHeight: 1};

// Intrinsic star size per `size` modifier — mirrors MUI Rating's prior
// small/medium/large icon sizes (implementation detail, no token exists for them).
const sizeFontSize: Record<'small' | 'medium' | 'large', string> = {
    small: '1.125rem', // 18px
    medium: '1.5rem', // 24px
    large: '1.875rem' // 30px
};

const starStyle: React.CSSProperties = {position: 'relative', display: 'inline-flex', width: '1em', height: '1em'};

const starSvgBaseStyle: React.CSSProperties = {display: 'block', width: '1em', height: '1em'};

const emptyStyle: React.CSSProperties = {...starSvgBaseStyle, fill: 'var(--rating-empty)'};

// The filled layer is overlaid on the empty star and clipped to the per-star
// fill width (set inline per-instance below) — this produces full and half stars.
const fillWrapperStyle: React.CSSProperties = {position: 'absolute', top: 0, left: 0, height: '100%', overflow: 'hidden'};

const filledStyle: React.CSSProperties = {...starSvgBaseStyle, fill: 'var(--rating-filled)'};

// MUI's Star (solid) and StarBorder (outline) geometry, inlined so a single icon
// does not pull in @mui/icons-material — the same approach as Chip's CloseGlyph.
// Both use the 0 0 24 24 viewBox. The empty star shows beneath; the filled star
// is overlaid and clipped to the per-star fill width to produce half-stars.
const STAR_FILLED = 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z';
const STAR_EMPTY =
    'M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z';

const StarSvg = ({style, path}: {readonly style: React.CSSProperties; readonly path: string}) => (
    <svg style={style} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d={path} />
    </svg>
);

export interface RatingProps {
    /** The rating value, e.g. 4.5. Clamped to [0, max]. */
    value: number;
    /** Number of stars. Defaults to 5. */
    max?: number;
    /** Intrinsic star size. Defaults to 'small' (the only size used today). */
    size?: 'small' | 'medium' | 'large';
    /** Consumer-owned visual override (layout, spacing, colour overrides). */
    className?: string;
    /** Builds the accessible label. Defaults to `${value} Star(s)`. */
    getLabelText?: (value: number, max: number) => string;
    /** Explicit accessible label; takes precedence over `getLabelText`. */
    'aria-label'?: string;
}

const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

const defaultLabel = (value: number) => `${value} Star${value === 1 ? '' : 's'}`;

function Rating({value, max = 5, size = 'small', className = '', getLabelText, 'aria-label': ariaLabel}: Readonly<RatingProps>) {
    const safeValue = clamp(value, 0, max);
    const label = ariaLabel ?? (getLabelText ? getLabelText(safeValue, max) : defaultLabel(safeValue));
    const rootStyle: React.CSSProperties = {...rootBaseStyle, fontSize: sizeFontSize[size], ...ratingVars};

    return (
        <span className={className} style={rootStyle} role="img" aria-label={label}>
            {Array.from({length: max}, (_, index) => {
                // Fraction of THIS star that is filled, as a percentage (the half-star
                // clip). Derived from `value`, so it is an inline implementation detail,
                // not a design value.
                const fill = clamp(safeValue - index, 0, 1) * 100;
                return (
                    <span key={index} style={starStyle}>
                        <StarSvg style={emptyStyle} path={STAR_EMPTY} />
                        <span style={{...fillWrapperStyle, width: `${fill}%`}}>
                            <StarSvg style={filledStyle} path={STAR_FILLED} />
                        </span>
                    </span>
                );
            })}
        </span>
    );
}

export default Rating;
